import React, { useState } from "react";
import { X, AlertTriangle, CheckCircle, Loader2 } from "lucide-react";
import {
  requestOrderCancellation,
  approveOrderCancellation,
} from "../../../api/salesService";

/**
 * CancelTransactionModal
 * @param {Object} transaction - Partial order with at least { id, status, transaction_number }
 * @param {Function} onClose
 * @param {Function} onSuccess - Called after successful action (to refresh parent list/detail)
 */
export default function CancelTransactionModal({
  transaction,
  onClose,
  onSuccess,
}) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  if (!transaction) return null;

  const isApprove = transaction.status === "CANCEL_REQUESTED";
  const isRequest = !isApprove; // PENDING, DONE, or any other status → show request form

  const handleSubmit = async () => {
    if (isRequest && !reason.trim()) {
      setError("Alasan pembatalan wajib diisi.");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      if (isRequest) {
        await requestOrderCancellation(transaction.id, {
          reason: reason.trim(),
        });
      } else {
        await approveOrderCancellation(transaction.id);
      }
      setDone(true);
      onSuccess?.();
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Terjadi kesalahan.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl w-full max-w-md shadow-2xl modal-panel-enter">
        {/* Header */}
        <div
          className={`px-6 py-4 flex justify-between items-center rounded-t-xl ${
            isApprove ? "bg-red-600" : "bg-orange-500"
          }`}>
          <div>
            <h2 className="text-base font-bold text-white">
              {isRequest
                ? "Ajukan Pembatalan Transaksi"
                : "Setujui Pembatalan Transaksi"}
            </h2>
            {transaction.transaction_number && (
              <p className="text-xs text-white/70 mt-0.5 font-mono">
                {transaction.transaction_number}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-1.5 rounded-full transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Success state */}
          {done ? (
            <div className="flex flex-col items-center py-6 gap-3 text-green-600">
              <CheckCircle className="w-12 h-12" />
              <p className="font-semibold text-base">
                {isRequest
                  ? "Pengajuan pembatalan berhasil dikirim."
                  : "Pembatalan berhasil disetujui."}
              </p>
              <button
                onClick={onClose}
                className="mt-2 px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">
                Tutup
              </button>
            </div>
          ) : (
            <>
              {/* Warning banner */}
              <div
                className={`flex gap-3 p-3 rounded-lg text-sm ${
                  isApprove
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-orange-50 text-orange-700 border border-orange-200"
                }`}>
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  {isRequest
                    ? "Pengajuan pembatalan akan mengubah status transaksi menjadi menunggu persetujuan."
                    : "Anda akan menyetujui pembatalan transaksi ini. Tindakan ini tidak dapat dibatalkan."}
                </span>
              </div>

              {/* Reason input (request only) */}
              {isRequest && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Alasan Pembatalan <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 h-24 resize-none"
                    placeholder="Contoh: Pelanggan membatalkan pesanan..."
                    value={reason}
                    onChange={(e) => {
                      setReason(e.target.value);
                      setError(null);
                    }}
                    disabled={loading}
                  />
                </div>
              )}

              {/* Error */}
              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium">
                  Batal
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`px-4 py-2 text-sm text-white rounded-lg font-medium flex items-center gap-2 ${
                    isApprove
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-orange-500 hover:bg-orange-600"
                  } disabled:opacity-60`}>
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isRequest ? "Ajukan Pembatalan" : "Setujui Pembatalan"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
